import { useState, useCallback, useEffect, useMemo } from 'react';
import api from '@helpers/api';
import { useCommunity } from 'contexts/community';

const PER_PAGE = 15;

export default function useChargebacks() {
    const { communities, loading: communitiesLoading } = useCommunity();

    const adminCommunities = useMemo(
        () => (communities || []).filter(c => c?.isAdmin()),
        [communities]
    );

    // Filters
    const [selectedCommunity, setSelectedCommunity] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    // Plans fetched from the API — /community/subscribed does not include plans
    const [allPlans, setAllPlans] = useState([]);
    const [plansLoading, setPlansLoading] = useState(false);

    useEffect(() => {
        if (communitiesLoading || adminCommunities.length === 0) return;

        let cancelled = false;
        setPlansLoading(true);

        Promise.all(
            adminCommunities.map(c =>
                api.get(`community/${c.id}/plan`)
                    .then(({ data }) => (data || []).map(p => ({ ...p, communityName: c.name, communityId: c.id })))
                    .catch(() => [])
            )
        ).then(results => {
            if (!cancelled) {
                setAllPlans(results.flat());
                setPlansLoading(false);
            }
        });

        return () => { cancelled = true; };
    }, [communitiesLoading, adminCommunities]);

    const plans = useMemo(() => {
        if (!selectedCommunity) return allPlans;
        return allPlans.filter(p => p.communityId === selectedCommunity);
    }, [allPlans, selectedCommunity]);

    // Chargebacks data
    const [chargebacks, setChargebacks] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        pages: 1,
        current_page: 1,
        total: 0,
    });

    const fetchChargebacks = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('per_page', PER_PAGE);

            if (selectedCommunity) params.append('filters[community]', selectedCommunity);
            if (selectedPlan) params.append('filters[plan]', selectedPlan);
            selectedStatuses.forEach(s => params.append('filters[statuses][]', s));

            const { data } = await api.get(`charge/chargeback/list?${params.toString()}`);

            setChargebacks(data.data || []);
            setSummary(data.summary || null);
            setPagination({
                pages: data.last_page || 1,
                current_page: data.current_page || 1,
                total: data.total || 0,
            });
        } catch (e) {
            console.error('Failed to fetch chargebacks', e);
            setChargebacks([]);
        } finally {
            setLoading(false);
        }
    }, [selectedCommunity, selectedPlan, selectedStatuses]);

    // Re-fetch on filter change, reset to page 1
    useEffect(() => {
        fetchChargebacks(1);
    }, [selectedCommunity, selectedPlan, selectedStatuses]);

    function handleCommunityChange(communityId) {
        setSelectedCommunity(communityId || null);
        setSelectedPlan(null);
    }

    function handlePlanChange(planId) {
        setSelectedPlan(planId || null);
    }

    function toggleStatus(statusId) {
        setSelectedStatuses(prev =>
            prev.includes(statusId)
                ? prev.filter(s => s !== statusId)
                : [...prev, statusId]
        );
    }

    function clearStatuses() {
        setSelectedStatuses([]);
    }

    function handlePageChange(page) {
        fetchChargebacks(page);
    }

    return {
        adminCommunities,
        plans,
        plansLoading,
        selectedCommunity,
        selectedPlan,
        selectedStatuses,
        chargebacks,
        summary,
        loading,
        pagination,
        handleCommunityChange,
        handlePlanChange,
        toggleStatus,
        clearStatuses,
        handlePageChange,
    };
}
